import { asIfAccessToken } from '@/features/as-if/asIfView';

/**
 * F-0709 ASIF-12 (`docs/concept-start-a-work-area-as-if.md` §2.6): the one seam through which the web
 * app writes the person's own device state — drafts, remembered tabs and widths, folds, keep-position,
 * the selected work area — to `localStorage` and `sessionStorage`.
 *
 * While this tab holds an as-if view, a write goes to a memory overlay and never reaches the browser's
 * store; a read sees the overlay first and then the person's own value, so the page behaves as if the
 * write had happened. The overlay is dropped the first time the seam is used after the view ends, so
 * the person's own values are what they were before the view, untouched.
 *
 * ⛔ A file outside this one names `localStorage`, `sessionStorage` or `indexedDB` only if it is in
 * `deviceStorage.guard.test.ts`'s exemption list with a reason. Never reach for `window.localStorage`
 * in a new feature: use `deviceLocal` / `deviceSession`.
 */
type AreaType = 'local' | 'session';

/** A removal under a view: the overlay says the key is gone without touching the person's own. */
const REMOVED = Symbol('removed');

class DeviceStorage {
  private overlay = new Map<string, string | typeof REMOVED>();

  constructor(private readonly area: AreaType) {}

  private real(): Storage | null {
    if (typeof window === 'undefined') return null;
    return this.area === 'local' ? window.localStorage : window.sessionStorage;
  }

  /** The view the overlay belongs to; another view, or none, starts it empty. */
  private overlayOf: string | null = null;

  /** True while a view is held; an overlay of any other view than the held one is dropped. */
  private held(): boolean {
    const token = asIfAccessToken();
    if (token !== this.overlayOf) {
      this.overlay.clear();
      this.overlayOf = token;
    }
    return token !== null;
  }

  private keys(): string[] {
    const real = this.real();
    const keys: string[] = [];
    for (let index = 0; real && index < real.length; index += 1) {
      const key = real.key(index);
      if (key !== null && this.overlay.get(key) !== REMOVED) keys.push(key);
    }
    for (const [key, value] of this.overlay) {
      if (value !== REMOVED && !keys.includes(key)) keys.push(key);
    }
    return keys;
  }

  get length(): number {
    return this.held() ? this.keys().length : (this.real()?.length ?? 0);
  }

  key(index: number): string | null {
    return this.held() ? (this.keys()[index] ?? null) : (this.real()?.key(index) ?? null);
  }

  getItem(key: string): string | null {
    if (this.held() && this.overlay.has(key)) {
      const value = this.overlay.get(key);
      return value === REMOVED || value === undefined ? null : value;
    }
    return this.real()?.getItem(key) ?? null;
  }

  setItem(key: string, value: string): void {
    if (this.held()) {
      this.overlay.set(key, String(value));
      return;
    }
    this.real()?.setItem(key, value);
  }

  removeItem(key: string): void {
    if (this.held()) {
      this.overlay.set(key, REMOVED);
      return;
    }
    this.real()?.removeItem(key);
  }

  clear(): void {
    if (this.held()) {
      for (const key of this.keys()) this.overlay.set(key, REMOVED);
      return;
    }
    this.real()?.clear();
  }
}

/** The person's device-wide state (survives a restart); a memory overlay under an as-if view. */
export const deviceLocal = new DeviceStorage('local') as unknown as Storage;

/** The tab's own state (ends with the tab); a memory overlay under an as-if view. */
export const deviceSession = new DeviceStorage('session') as unknown as Storage;

/** True when the app may open IndexedDB for the person's own files: it exists and no view is held. */
export function canOpenDeviceDatabase(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.indexedDB !== 'undefined' &&
    asIfAccessToken() === null
  );
}
